import { useCallback, useMemo, useReducer } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { GetDisbursementPayoutRequestsParams } from 'api';

type SortingState = '-date';

type PaginationState = {
  includeCursor?: boolean;
  cursorBefore: string | undefined;
  cursorAfter: string | undefined;
};

export type LocalState = PaginationState & {
  sorting: SortingState;
} & { batchId: string };

type Action =
  | { type: 'setCursorBefore'; value: string | undefined }
  | { type: 'setCursorAfter'; value: string | undefined };

function reducer(state: LocalState, action: Action): LocalState {
  switch (action.type) {
    case 'setCursorBefore':
      return {
        ...state,
        cursorBefore: action.value,
        cursorAfter: undefined,
        includeCursor: undefined
      };
    case 'setCursorAfter':
      return {
        ...state,
        cursorAfter: action.value,
        cursorBefore: undefined,
        includeCursor: undefined
      };
  }
}

const setCursorAfter = (value: string | undefined): Action => ({
  type: 'setCursorAfter',
  value
});

export function useLocalState(defaultState?: Partial<LocalState>) {
  const [searchParams] = useSearchParams();

  const initialState = useMemo<LocalState>(
    () => ({
      sorting: (searchParams.get('sort') as SortingState) || '-date',
      includeCursor: defaultState?.includeCursor || undefined,
      cursorBefore:
        defaultState?.cursorBefore ||
        searchParams.get('cursorBefore') ||
        undefined,
      cursorAfter:
        defaultState?.cursorAfter ||
        searchParams.get('cursorAfter') ||
        undefined,
      batchId: defaultState?.batchId || ''
    }),
    []
  );

  const [state, dispatch] = useReducer(reducer, initialState);

  const queryParams = useMemo(
    () => ({
      cursorAfter: state?.cursorAfter,
      cursorBefore: state?.cursorBefore,
      sort: state.sorting
    }),
    [state]
  );

  const updateCursorAfter = useCallback(
    (value: string | undefined) => dispatch(setCursorAfter(value)),
    [dispatch]
  );

  return {
    state,
    queryParams,
    updateCursorAfter
  };
}

export const stateToRequestParams = (
  state: Partial<LocalState>
): GetDisbursementPayoutRequestsParams => {
  const params = {} as GetDisbursementPayoutRequestsParams;
  if (state?.batchId) params.batch_id = state.batchId;
  if (state?.sorting) params.sort = state.sorting;
  if (state?.cursorAfter) params.cursor_after = state.cursorAfter;
  if (state?.cursorBefore) params.cursor_before = state.cursorBefore;
  if (state?.includeCursor)
    params.include_cursor = state.includeCursor.toString();

  params.page_size = '100';

  return params;
};
